import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FolderDto, GalleryDto } from '@nusign/picflow-backend-types';
import { filter, map, switchMap, take } from 'rxjs';
import { FinderComponent } from '../../../account-gallery-shared/components/finder/finder.component';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { IsGallery } from '../../../lib/is-gallery';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { DialogService, OnGetData } from '../../../services/dialog.service';
import { FolderService } from '../../../services/folder.service';
import { GalleryService } from '../../../services/gallery.service';
import { MoveGalleryService } from '../../../services/move-gallery.service';
import { StateService } from '../../../services/state.service';
import { SubscriptionService } from '../../../services/subscription.service';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';

@Component({
  selector: 'app-move-gallery-dialog',
  templateUrl: './move-gallery-dialog.component.html',
  styleUrls: ['./move-gallery-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FinderComponent, ButtonDirective, LoadingIndicatorComponent, BillingScopePipe, RouterLink, SvgComponent],
})
export class MoveGalleryDialogComponent implements OnGetData {
  public state = inject(StateService);
  private folderService = inject(FolderService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private galleryService = inject(GalleryService);
  private moveGalleryService = inject(MoveGalleryService);
  private subscriptionService = inject(SubscriptionService);
  private dialog = inject(DialogService);
  dialogName = 'move-gallery';
  close: (...args) => void;
  gallery = signal<GalleryDto>(undefined);
  allFolders = toSignal(this.folderService.getFolders());
  allGalleries = toSignal(this.galleryService.getAllGalleries({ ignorePublicAccessLevel: true }));
  selectedFolder = computed<FolderDto>(() => {
    const moveTo = this.moveTo();
    const allGalleries = this.allGalleries();
    const allFolders = this.allFolders();
    if (!allGalleries || !allFolders || !moveTo) return;
    if (!IsGallery(moveTo)) return <FolderDto>moveTo;
    const moveToGallery = moveTo as GalleryDto;
    if (moveToGallery.folder) return allFolders.find(f => f.id === moveToGallery.folder);
    if (moveToGallery.parent_gallery_id) {
      const parentGallery = allGalleries.find(g => g.id === moveToGallery.parent_gallery_id);
      if (parentGallery.folder) return allFolders.find(f => f.id === parentGallery.folder);
    }
  });
  selectedGallery = computed<GalleryDto>(() => {
    const moveTo = this.moveTo();
    const allGalleries = this.allGalleries();
    if (!moveTo || !allGalleries) return;
    if (!IsGallery(moveTo)) return;
    const moveToGallery = moveTo as GalleryDto;
    if (moveToGallery.parent_gallery_id) return allGalleries.find(g => g.id === moveToGallery.parent_gallery_id);
    return moveToGallery;
  });
  loading = signal(false);
  form = new FormGroup({
    newFolderName: new FormControl(),
  });
  moveTo = signal<GalleryDto | FolderDto>(undefined);
  hasSubGalleries = toSignal(
    toObservable(this.gallery).pipe(
      filter(Boolean),
      switchMap(g => this.galleryService.getSubGalleries(g.id)),
      map(subGalleries => !!subGalleries.length)
    )
  );
  initializing = computed(() => {
    const allGalleries = this.allGalleries();
    const allFolders = this.allFolders();
    return !allGalleries && !allFolders;
  });
  mainPrice = toSignal(this.subscriptionService.getMainPrice());

  dialogOptions(): DialogOptions {
    return { size: 640, spacingX: 40, spacingY: 40 };
  }

  async onGetData(
    data: {
      gallery: GalleryDto;
      selectedFolder?: FolderDto;
      moveTo: GalleryDto | FolderDto;
    },
    close: (...args) => void
  ) {
    this.gallery.set(data.gallery);
    if (data.moveTo) {
      this.moveTo.set(data.moveTo);
    } else {
      if (data.gallery.folder) {
        this.folderService
          .getFolders()
          .pipe(take(1))
          .subscribe(folders => {
            this.moveTo.set(folders.find(f => f.id === data.gallery.folder));
          });
      } else if (data.gallery.parent_gallery_id) {
        this.galleryService
          .getAllGalleries({ ignorePublicAccessLevel: true })
          .pipe(take(1))
          .subscribe(galleries => {
            this.moveTo.set(galleries.find(g => g.id === data.gallery.parent_gallery_id));
          });
      }
    }
    this.close = close;
  }

  onSelectFolder(folder: FolderDto) {
    this.moveTo.set(folder);
  }

  onSelectGallery(gallery: GalleryDto) {
    if (gallery) this.moveTo.set(gallery);
  }

  async submit() {
    if (this.loading()) return;
    const from = (this.gallery().folder as string) || this.gallery().parent_gallery_id;
    const to = (this.selectedGallery()?.id as string) || (this.selectedFolder()?.id as string);
    if (from === to) return this.close();
    this.loading.set(true);
    this.moveGalleryService.moveGallery(this.gallery(), this.selectedGallery(), this.selectedFolder()).subscribe(() => {
      this.analytics.track('gallery_moved', true, {
        from,
        to: this.selectedGallery()?.id || this.selectedFolder()?.id,
      });
      this.close(true);
    });
  }

  reOpenFn() {
    const { gallery, selectedFolder, moveTo, hasSubGalleries } = this;
    return (_gallery: GalleryDto, _folder: FolderDto) => {
      if (hasSubGalleries()) _gallery = undefined;
      this.dialog.open(MoveGalleryDialogComponent, {
        gallery: gallery(),
        moveTo: _folder || _gallery || moveTo(),
        selectedFolder: _folder || selectedFolder(),
      });
    };
  }
}
